import { useState } from "react"
import { Box, ButtonBase, CircularProgress, Drawer, IconButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowsRotate, faCircleExclamation, faEllipsisV, faList, faLock } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { useApiQuery } from "../../../core/api/useApiQuery"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { getContactInfo } from "../../../modules/merchant/merchant/production/meetingService/components/slipModal/utils"
import {
    fetchOrderDetails,
    MeetingDetailsOrder,
    MeetingServiceQuery,
} from "../../../modules/nonAdminUser/reports/home/meetingServiceApi"
import type { OrderRecurrence } from "../../../modules/nonAdminUser/reports/home/weekMenuTypes"
import { StandardChip } from "../badges/StandardChip"
import { StandardButton } from "../buttons/StandardButton"
import { DrawerDetailRow } from "../drawers/DrawerDetailRow"
import { DrawerDetailsCard } from "../drawers/DrawerDetailsCard"
import { DrawerSection } from "../meetingServiceOrderDrawer/DrawerSection"
import { OrderDrawerHeader } from "../meetingServiceOrderDrawer/OrderDrawerHeader"

import { formatRecurrencePattern, formatSeriesPeriod, SeriesOrderRow, toSeriesOrderRows } from "./recurrenceSeriesLib"
import { RecurrenceSeriesOrderActions, RecurrenceSeriesOrderMenu } from "./RecurrenceSeriesOrderMenu"

const useStyles = makeStyles()((theme) => ({
    paper: {
        width: "max(40rem, 50vw)",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
    },
    subtitle: {
        color: theme.palette.tailwind.slate[500],
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "1rem",
        background: theme.palette.tailwind.slate[100],
    },
    stateBox: {
        height: "100%",
    },
    errorIcon: {
        fontSize: "1.75rem",
        color: theme.palette.tailwind.slate[400],
    },
    mutedText: {
        color: theme.palette.tailwind.slate[500],
    },
    ordersTable: {
        padding: "0 1rem .5rem",
    },
    ordersRow: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[100]}`,
        "&:last-child": { borderBottom: "none" },
    },
    ordersGrid: {
        display: "grid",
        flex: 1,
        minWidth: 0,
        gridTemplateColumns: "6.5rem minmax(0, 1fr)",
        alignItems: "center",
        columnGap: "1rem",
        textAlign: "left",
    },
    ordersHeader: {
        padding: ".75rem 0",
        fontWeight: theme.typography.fontWeightMedium,
        color: theme.palette.tailwind.slate[500],
    },
    orderRow: {
        minHeight: "3.25rem",
        padding: ".5rem 0",
        color: theme.palette.tailwind.slate[800],
        "&:hover": {
            background: theme.palette.tailwind.slate[50],
        },
    },
    menuButtonSpacer: {
        width: "2.5rem",
        flexShrink: 0,
    },
    menuButton: {
        width: "2.5rem",
        height: "2.5rem",
        flexShrink: 0,
        color: theme.palette.tailwind.slate[600],
    },
    orderRowCurrent: {
        fontWeight: theme.typography.fontWeightBold,
    },
    orderRowLocked: {
        color: theme.palette.tailwind.slate[500],
    },
    lockedLabel: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".25rem",
        color: theme.palette.tailwind.slate[500],
    },
    emptyText: {
        padding: "1rem 0",
        color: theme.palette.tailwind.slate[500],
    },
    footer: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
}))

type SeriesDetails = Readonly<{
    order: MeetingDetailsOrder
    recurrence?: OrderRecurrence
    seriesOrders: ReadonlyArray<SeriesOrderRow>
    hasSeriesOrders: boolean
}>

type OrderMenuState = Readonly<{
    anchorEl: HTMLElement
    order: SeriesOrderRow
}>

export type RecurrenceSeriesDrawerOrder = Readonly<{
    id: number
    meetingDetails: Readonly<{ meetingName?: string | null; recurrence?: OrderRecurrence }>
}>

type Props = Readonly<{
    open: boolean
    order: RecurrenceSeriesDrawerOrder
    onOpenOrder: (orderId: number) => void
    orderActions?: RecurrenceSeriesOrderActions
    onClose: () => void
}>

export function RecurrenceSeriesDrawer({ open, order, onOpenOrder, orderActions, onClose }: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const [isOrdersOpen, setIsOrdersOpen] = useState(true)
    const [orderMenu, setOrderMenu] = useState<OrderMenuState | null>(null)

    const detailsQuery = useApiQuery(fetchOrderDetails, {
        queryName: MeetingServiceQuery.ORDER_DETAILS,
        dependencies: { orderId: order.id },
        enabled: open,
        select: (data): SeriesDetails | undefined => {
            const orderDetails = data.orders?.[0]
            if (!orderDetails) return undefined

            const detailsRecurrence = orderDetails.meetingDetails.recurrence
            const listRecurrence = order.meetingDetails.recurrence
            const seriesOrders = detailsRecurrence?.orders ?? listRecurrence?.orders

            return {
                order: orderDetails,
                recurrence: detailsRecurrence ?? listRecurrence,
                seriesOrders: toSeriesOrderRows(seriesOrders ?? [], dateTimeFactory),
                hasSeriesOrders: seriesOrders !== undefined,
            }
        },
    })
    const details = detailsQuery.data
    const hasNoOrder = detailsQuery.isError || (!detailsQuery.isInitialLoading && !details)
    const seriesCount = details
        ? details.hasSeriesOrders
            ? details.seriesOrders.length
            : undefined
        : order.meetingDetails.recurrence?.orders?.length

    return (
        <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.paper }}>
            <OrderDrawerHeader
                title={order.meetingDetails.meetingName ?? undefined}
                subtitle={
                    <Stack direction="row" alignItems="center" spacing=".375rem" className={classes.subtitle}>
                        <FontAwesomeIcon icon={faArrowsRotate} size="sm" />
                        {seriesCount === undefined ? (
                            <LocalizedStrict id="meeting-service-recurrence-series-drawer-subtitle-no-count">
                                <Typography variant="body2">Recurring series</Typography>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict
                                id="meeting-service-recurrence-series-drawer-subtitle"
                                vars={{ count: seriesCount }}
                            >
                                <Typography variant="body2">{`Recurring series · ${seriesCount} orders`}</Typography>
                            </LocalizedStrict>
                        )}
                    </Stack>
                }
                actions={[]}
                onClose={onClose}
            />

            <Box className={classes.body}>
                {hasNoOrder ? (
                    <Stack alignItems="center" justifyContent="center" spacing="1rem" className={classes.stateBox}>
                        <FontAwesomeIcon icon={faCircleExclamation} className={classes.errorIcon} />
                        <LocalizedStrict id="meeting-service-order-drawer-load-error">
                            <Typography variant="body2" className={classes.mutedText}>
                                Couldn`t load the order
                            </Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="meeting-service-order-drawer-retry">
                            <StandardButton
                                fullWidth={false}
                                variant="outlined"
                                color="grey"
                                onClick={() => detailsQuery.refetch()}
                            >
                                Try again
                            </StandardButton>
                        </LocalizedStrict>
                    </Stack>
                ) : !details ? (
                    <Stack alignItems="center" justifyContent="center" className={classes.stateBox}>
                        <CircularProgress />
                    </Stack>
                ) : (
                    <>
                        <SeriesDetailsCard details={details} />

                        <DrawerSection
                            icon={faList}
                            titleId="meeting-service-recurrence-series-drawer-orders-title"
                            titleFallback="Orders in series"
                            count={details.seriesOrders.length}
                            open={isOrdersOpen}
                            onToggle={() => setIsOrdersOpen((prev) => !prev)}
                        >
                            <Box className={classes.ordersTable}>
                                {details.seriesOrders.length === 0 ? (
                                    <LocalizedStrict id="meeting-service-recurrence-series-drawer-orders-empty">
                                        <Typography variant="body2" className={classes.emptyText}>
                                            No orders found in this series
                                        </Typography>
                                    </LocalizedStrict>
                                ) : (
                                    <>
                                        <Box className={classes.ordersRow}>
                                            <Box className={cx(classes.ordersGrid, classes.ordersHeader)}>
                                                <LocalizedStrict id="meeting-service-recurrence-series-drawer-orders-id-column">
                                                    <Typography variant="body2" component="span">
                                                        Order ID
                                                    </Typography>
                                                </LocalizedStrict>
                                                <LocalizedStrict id="meeting-service-recurrence-series-drawer-orders-date-column">
                                                    <Typography variant="body2" component="span">
                                                        Delivery date
                                                    </Typography>
                                                </LocalizedStrict>
                                            </Box>
                                            {orderActions && <Box className={classes.menuButtonSpacer} />}
                                        </Box>

                                        {details.seriesOrders.map((seriesOrder) => {
                                            const isCurrent = seriesOrder.id === order.id

                                            return (
                                                <Box key={seriesOrder.id} className={classes.ordersRow}>
                                                    <ButtonBase
                                                        className={cx(classes.ordersGrid, classes.orderRow, {
                                                            [classes.orderRowCurrent]: isCurrent,
                                                            [classes.orderRowLocked]: seriesOrder.isLocked,
                                                        })}
                                                        onClick={() => onOpenOrder(seriesOrder.id)}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            component="span"
                                                            fontWeight="inherit"
                                                        >
                                                            {seriesOrder.id}
                                                        </Typography>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            flexWrap="wrap"
                                                            gap=".625rem"
                                                            component="span"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                component="span"
                                                                fontWeight="inherit"
                                                            >
                                                                {`${dateTimeFactory.formatDayAndDate(seriesOrder.deliveryTime)} · ${dateTimeFactory.formatTime(seriesOrder.deliveryTime)}`}
                                                            </Typography>
                                                            {isCurrent && (
                                                                <LocalizedStrict id="meeting-service-recurrence-series-drawer-current-order">
                                                                    <StandardChip
                                                                        variant="success"
                                                                        placement="table"
                                                                        size="small"
                                                                    >
                                                                        This order
                                                                    </StandardChip>
                                                                </LocalizedStrict>
                                                            )}
                                                            {seriesOrder.isLocked && (
                                                                <span className={classes.lockedLabel}>
                                                                    <FontAwesomeIcon icon={faLock} size="sm" />
                                                                    <LocalizedStrict id="meeting-service-recurrence-series-drawer-locked">
                                                                        <Typography variant="caption" component="span">
                                                                            Locked
                                                                        </Typography>
                                                                    </LocalizedStrict>
                                                                </span>
                                                            )}
                                                        </Stack>
                                                    </ButtonBase>
                                                    {orderActions && (
                                                        <IconButton
                                                            size="small"
                                                            className={classes.menuButton}
                                                            aria-label={l10n.getString(
                                                                "meeting-service-recurrence-series-drawer-order-menu-button",
                                                                { orderId: String(seriesOrder.id) },
                                                            )}
                                                            aria-haspopup="menu"
                                                            aria-expanded={orderMenu?.order.id === seriesOrder.id}
                                                            onClick={(event) =>
                                                                setOrderMenu({
                                                                    anchorEl: event.currentTarget,
                                                                    order: seriesOrder,
                                                                })
                                                            }
                                                        >
                                                            <FontAwesomeIcon icon={faEllipsisV} size="sm" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            )
                                        })}
                                    </>
                                )}
                            </Box>
                        </DrawerSection>
                    </>
                )}
            </Box>

            {orderActions && orderMenu && (
                <RecurrenceSeriesOrderMenu
                    anchorEl={orderMenu.anchorEl}
                    orderId={orderMenu.order.id}
                    isLocked={orderMenu.order.isLocked}
                    actions={orderActions}
                    onViewOrder={onOpenOrder}
                    onClose={() => setOrderMenu(null)}
                />
            )}

            <Stack direction="row" justifyContent="flex-end" className={classes.footer}>
                <LocalizedStrict id="meeting-service-recurrence-series-drawer-close-button">
                    <StandardButton fullWidth={false} variant="contained" onClick={onClose}>
                        Close
                    </StandardButton>
                </LocalizedStrict>
            </Stack>
        </Drawer>
    )
}

function SeriesDetailsCard({ details }: Readonly<{ details: SeriesDetails }>) {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const { order: orderDetails, recurrence, seriesOrders } = details
    const contactName =
        getContactInfo(orderDetails.organizers, orderDetails.customer).meetingHost?.name ??
        orderDetails.customer?.displayName ??
        ""
    const sittings = orderDetails.deliveryDetails.sittings ?? 0
    const deliveryType = orderDetails.deliveryDetails.type
    const locationName = orderDetails.deliveryDetails.location?.name
    const period = formatSeriesPeriod(seriesOrders, dateTimeFactory)

    return (
        <DrawerDetailsCard>
            {recurrence && (
                <DrawerDetailRow
                    label={
                        <LocalizedStrict id="meeting-service-recurrence-series-drawer-recurrence">
                            <span>Recurrence</span>
                        </LocalizedStrict>
                    }
                    value={formatRecurrencePattern(recurrence, l10n, dateTimeFactory)}
                />
            )}
            {period && (
                <DrawerDetailRow
                    label={
                        <LocalizedStrict id="meeting-service-recurrence-series-drawer-period">
                            <span>Period</span>
                        </LocalizedStrict>
                    }
                    value={period}
                />
            )}
            <DrawerDetailRow
                label={
                    <LocalizedStrict id="meeting-service-recurrence-series-drawer-contact">
                        <span>Contact</span>
                    </LocalizedStrict>
                }
                value={contactName}
            />
            <DrawerDetailRow
                label={
                    <LocalizedStrict id="meeting-service-recurrence-series-drawer-covers">
                        <span>Covers</span>
                    </LocalizedStrict>
                }
                value={
                    <LocalizedStrict
                        id="meeting-service-recurrence-series-drawer-covers-value"
                        vars={{ count: sittings }}
                    >
                        <span>{`${sittings} people`}</span>
                    </LocalizedStrict>
                }
            />
            {deliveryType && (
                <DrawerDetailRow
                    label={
                        <LocalizedStrict id="meeting-service-recurrence-series-drawer-delivery-type">
                            <span>Delivery type</span>
                        </LocalizedStrict>
                    }
                    value={l10n.getStringForEnum("delivery-type-filter", deliveryType, deliveryType)}
                />
            )}
            {locationName && (
                <DrawerDetailRow
                    label={
                        <LocalizedStrict id="meeting-service-recurrence-series-drawer-location">
                            <span>Delivery location</span>
                        </LocalizedStrict>
                    }
                    value={locationName}
                />
            )}
        </DrawerDetailsCard>
    )
}
